import { type FC, memo, useContext, useEffect, useState } from 'react';

import ContractsGridContext from '@/contexts/contractsGridContext';

import './styles.css';
import type { Props } from './types';

const controlClass =
  'h-7 w-full rounded border border-uniform-gray-300 bg-white px-1.5 py-0 text-xs dark:border-uniform-gray-700 dark:bg-uniform-gray-900';

/**
 * A floating filter row cell for the contracts grid. It writes straight to the URL params the
 * list request reads, rather than to AG Grid's filter model, since filtering runs server-side.
 */
const ContractGridFloatingFilter: FC<Props> = ({ kind, optionsKey, param, placeholder }) => {
  const { filterOptions, getParam, setParam } = useContext(ContractsGridContext);
  const value = getParam(param);
  const [text, setText] = useState(value);

  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (kind !== 'text' || text === value) return;
    const timeout = setTimeout(() => setParam(param, text), 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  if (kind === 'choice') {
    const choices = (optionsKey && filterOptions?.[optionsKey]) || [];
    return (
      <select
        aria-label={placeholder}
        className={controlClass}
        onChange={(event) => setParam(param, event.target.value)}
        value={value}>
        <option value="">Any</option>
        {choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      aria-label={placeholder}
      className={controlClass}
      onChange={(event) =>
        kind === 'date' ? setParam(param, event.target.value) : setText(event.target.value)
      }
      placeholder={placeholder}
      title={placeholder}
      type={kind === 'date' ? 'date' : 'text'}
      value={kind === 'date' ? value : text}
    />
  );
};

export default memo(ContractGridFloatingFilter);
